import React, { useEffect, useRef, useState } from "react";
import { isMobile } from 'react-device-detect';
import TrackingAPI from "api/TrackingAPI";
import AgeIcon from 'assets/character/age.svg';
import HeightIcon from 'assets/character/height.svg';
import HobbiesIcon from 'assets/character/hobbies.svg';
import JobIcon from 'assets/character/job.svg';
import NationalityIcon from 'assets/character/nationality.svg';
import SexIcon from 'assets/character/sex.svg';
import SexualPreferences from 'assets/character/sexualPreferences.svg';
import WeightIcon from 'assets/character/weight.svg';
import { analyticsService } from "services/analyticsService";
import styled from "styled-components";
import { ICharacter } from "types/characters";

interface ICharacterCardProps {
  character: ICharacter
}
const DESCRIPTION_LIMIT = 90;

const iconMap: Record<string, string> = {
  sex: SexIcon,
  height: HeightIcon,
  weight: WeightIcon,
  job: JobIcon,
  sexualPreferences: SexualPreferences,
  age: AgeIcon,
  nationality: NationalityIcon,
  hobbies: HobbiesIcon,
};

const renderPersonalCharacteristics = (characteristics: Record<string, string> | undefined) => {
  if (!characteristics) return null;

  return Object.entries(characteristics)

    .filter(([_, value]) => value && value.trim() !== '')
    .map(([key, value]) => (
      <Tag key={key}>
        <Icon src={iconMap[key]} alt={key} />
        {value}
      </Tag>
    ));
};

const CharacterCard: React.FC<ICharacterCardProps> = (props) => {
  const { character } = props;
  const [expanded, setExpanded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasTrackedRef = useRef(false);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const isLongDescription = character.description.length > DESCRIPTION_LIMIT;
  const shortDescription = isLongDescription
    ? character.description.slice(0, DESCRIPTION_LIMIT) + "..."
    : character.description;

  useEffect(() => {
    const trackEvent = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const analyticsEvent = {
        character_id: character.id,
        character_name: character.name,
      };

      try {
        TrackingAPI.trackEvent('character_card_view', analyticsEvent);
        analyticsService.trackGoogleEvent('character_card_view', analyticsEvent);
      } catch (error) {
        throw error;
      }
    };

    trackEvent();
  }, [character.id, character.name, character.videoCoverUrl]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (isMobile) {
            setIsVisible(entry.isIntersecting);
            setShouldLoadVideo(true);
          }
          if (entry.isIntersecting && !isMobile) {
            setShouldLoadVideo(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        videoRef.current.play().catch(() => { });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isVisible]);

  return (
    <Card>
      <AvatarSection
        ref={containerRef}
        onMouseEnter={() => {
          if (character?.videoCoverUrl && !isMobile) {
            setIsVisible(true);
          }
        }}
        onMouseLeave={() => {
          if (character?.videoCoverUrl && !isMobile) {
            setIsVisible(false);
          }
        }}
      >
        <AvatarSquare src={character.coverURL} alt={`${character.name}'s avatar`} />
        {character?.videoCoverUrl && shouldLoadVideo && (
          <CardVideo
            visible={isVisible}
            ref={videoRef}
            muted
            playsInline
            preload={isMobile ? "none" : "auto"}
            onEnded={() => setIsVisible(false)}
          >
            <source src={character.videoCoverUrl} type="video/mp4" />
          </CardVideo>
        )}
      </AvatarSection>
      <ContentSection>
        <Name>{character.name}</Name>
        <DescriptionBlock>
          <Description expanded={expanded}>
            {expanded || !isLongDescription ? character.description : shortDescription}
          </Description>
          {isLongDescription && (
            <ExpandBtn onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? 'Hide' : 'Expand'}
            </ExpandBtn>
          )}
        </DescriptionBlock>
        <AboutSection>
          <AboutTitle>About</AboutTitle>
          <TagWrapper>
            {renderPersonalCharacteristics(character.personalCharacteristics)}
          </TagWrapper>
        </AboutSection>
      </ContentSection>
    </Card>
  );
};

export default CharacterCard;

const Card = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  margin: 0 auto;
  color: #fff;
  background: #181818;
  min-height: 336px;
  position: relative;

  @media (max-width: 600px) {
    flex-direction: column;
    border-radius: 12px;
    min-height: unset;
    max-width: 343px;
    margin: 0 auto 24px auto;
    background: #181818;
    padding-bottom: 24px;
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.10);
  }
`;

const AvatarSection = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #232323;
  width: 261px;
  height: 336px;

  @media (max-width: 600px) {
    width: 100%;
    height: 441px;
    background: none;
    padding: 0;
    aspect-ratio: 7/9;
    border-radius: 12px 12px 0 0;
    overflow: hidden;
  }
`;

const AvatarSquare = styled.img`
  width: 261px;
  height: 336px;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;

  @media (max-width: 600px) {
    width: 100%;
    height: 100%;
    aspect-ratio: 7/9;
    border-radius: 12px 12px 0 0;
    max-width: 343px;
    max-height: 441px;
    min-height: 220px;
    object-fit: cover;
    display: block;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 32px 32px 32px 40px;
  flex: 1;
  gap: 18px;
  max-height: 336px;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 20px 20px 0 20px;
    align-items: flex-start;
    gap: 18px;
    height: auto;
    overflow-y: visible;
    max-height: none;
  }
`;

const Name = styled.h2`
  font-weight: 700;
  margin: 0;
  color: #fff;
  font-family: Raleway;
  font-size: 20px;
  font-weight: 700;
  line-height: 22px;

  @media (max-width: 600px) {
    text-align: left;
    font-size: 24px;
    line-height: 28px;
    margin-top: 24px;
    margin-bottom: 12px;
  }
`;

const DescriptionBlock = styled.div`
  width: 100%;
`;

interface DescriptionProps {
  expanded: boolean;
}

const Description = styled.p<DescriptionProps>`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: #FFF;
  margin: 0;

  @media (max-width: 600px) {
    text-align: left;
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 0;
    max-width: 100%;
    ${({ expanded }) =>
    !expanded && `
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
  }
`;

const ExpandBtn = styled.button`
  background: none;
  border: none;
  color: var(--Text-Color_text_secondary, #A9A9A9);
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  text-decoration: underline;
  text-align: left;
  margin-top: 12px;
  margin-bottom: 0;
  cursor: pointer;
  padding: 0;
  display: block;
`;

const AboutSection = styled.div`
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const AboutTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: #696969;
  margin-bottom: 10px;
`;

const TagWrapper = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 18px;

  @media (max-width: 600px) {
    justify-content: flex-start;
    gap: 10px;
  }
`;

const Tag = styled.div`
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: var(--Base-Color_bg_tetriary, #131313);
  color: var(--Text-Color_text_secondary, #A9A9A9);
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  text-align: center;

  @media (max-width: 600px) {
    padding: 6px 8px;
    min-width: unset;
    min-height: 28px;
    gap: 10px;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    line-height: 18px;
  }
`;

const Icon = styled.img`
  width: 18px;
  height: 18px;
  display: inline-block;

  @media (max-width: 600px) {
    width: 16px;
    height: 16px;
  }
`;

const CardVideo = styled.video<{ visible: boolean }>`
  width: 261px;
  height: 336px;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => (props?.visible ? '1' : '0')};
  transition: opacity 0.3s ease;

  @media (max-width: 600px) {
    width: 100%;
    height: 100%;
    aspect-ratio: 7/9;
    border-radius: 12px 12px 0 0;
    max-width: 343px;
    max-height: 441px;
    min-height: 220px;
    object-fit: cover;
    display: block;
  }
`;